import { Chess } from "@vendor/chess.js";
import type { BlackStructurePlan, PieceSpec } from "./types.ts";

/**
 * Structure plans: which plan fits the board, and what it says once the moves
 * already played are taken into account.
 */
type Color = "w" | "b";

const TYPES: Record<string, string> = { P: "p", N: "n", B: "b", R: "r", Q: "q", K: "k" };

function pieceOn(chess: Chess, spec: PieceSpec, color: Color): boolean {
  const type = TYPES[spec[0]];
  const square = spec.slice(1);
  if (!type || !/^[a-h][1-8]$/.test(square)) return false;
  const piece = chess.get(square as never);
  return Boolean(piece && piece.type === type && piece.color === color);
}

function pawn(chess: Chess, square: string, color: Color): boolean {
  return pieceOn(chess, `P${square}`, color);
}

/** True once the king has castled: it has left e8 and no castling right remains. */
export function blackCastled(chess: Chess): boolean {
  const rights = chess.fen().split(" ")[2] || "";
  if (/[kq]/.test(rights)) return false;
  const king = chess.get("e8" as never);
  return !(king && king.type === "k" && king.color === "b");
}

export function planMatches(when: BlackStructurePlan["when"], chess: Chess): boolean {
  return (
    (when.blackPawns || []).every((sq) => pawn(chess, sq, "b")) &&
    (when.whitePawns || []).every((sq) => pawn(chess, sq, "w")) &&
    !(when.noBlackPawns || []).some((sq) => pawn(chess, sq, "b")) &&
    !(when.noWhitePawns || []).some((sq) => pawn(chess, sq, "w")) &&
    (when.blackPieces || []).every((spec) => pieceOn(chess, spec, "b")) &&
    (when.whitePieces || []).every((spec) => pieceOn(chess, spec, "w")) &&
    !(when.noBlackPieces || []).some((spec) => pieceOn(chess, spec, "b")) &&
    !(when.noWhitePieces || []).some((spec) => pieceOn(chess, spec, "w")) &&
    (when.blackCastled === undefined || when.blackCastled === blackCastled(chess))
  );
}

export type PlanGuide = { goal: string; piece: string; next: string; watch: string };

/**
 * The plan's advice for this board: only the steps not yet played are named.
 * `color` is the student's side, whose pieces the steps describe.
 */
export function planGuide(plan: BlackStructurePlan, chess: Chess, color: Color = "b"): PlanGuide {
  // A step is done once any listed piece stands on its square; a spec with a
  // leading "!" counts when that piece is gone (the pawn has moved on).
  const achieved = (spec: PieceSpec) =>
    spec.startsWith("!") ? !pieceOn(chess, spec.slice(1), color) : pieceOn(chess, spec, color);
  const pending = (plan.steps || []).filter((step) => !step.done.some(achieved));
  return {
    goal: plan.goal,
    piece: plan.piece,
    next: pending.length ? pending.map((step) => step.text).join(" ") : plan.next,
    watch: plan.watch,
  };
}

/** The first plan in course order that fits the board. */
export function structurePlan(
  plans: BlackStructurePlan[],
  chess: Chess,
  color: Color = "b"
): PlanGuide | null {
  const plan = plans.find((candidate) => planMatches(candidate.when, chess));
  return plan ? planGuide(plan, chess, color) : null;
}
